import Cookies from 'js-cookie'

import { captureExceptionWithMessage } from './logRocket'

/**
 * @name LocalPersistence
 * @description A utility method for setting/getting values from local storage.
 * @example
 * import { LocalPersistence } from 'utils/localPersistence'
 * LocalPersistence.set('key', 'value')
 * LocalPersistence.get('key')
 * LocalPersistence.remove('key')
 *
 */

/**
 *
 * Change log:
 * - 01/06/2021: Default to localStorage, fallback to cookies to retrieve values and set values in localStorage to phase out cookies
 *
 */

const LocalPersistence = {
  /**
   * Generate a prefixed key for the local persistence
   * @param {string} key - The key to prefix
   */
  _generateKey(key: string) {
    return `drift_account_activity_${key}`
  },
  /**
   * Get a key localStorage
   * @param {string} key - The key to get from local persistence
   * @returns {string | undefined} The value of the key, undefined if not found
   **/
  get(key: string) {
    const val = this._generateKey(key)
    try {
      const cookie = Cookies.get(val)
      // Phase out cookies in favor of localStorage
      if (cookie) {
        this.set(key, cookie)
        Cookies.remove(val)
      }
      return global.localStorage.getItem(val) || cookie
    } catch (e) {
      captureExceptionWithMessage('Error getting from local storage', e)
    }
  },
  /**
   * Set a key in local storage
   * @param {string} key - The key to set
   * @param {string} value - The value to set
   */
  set(key: string, value: string | number | boolean) {
    const val = this._generateKey(key)
    try {
      global.localStorage.setItem(val, `${value}`)
    } catch (e) {
      captureExceptionWithMessage('Error setting to local storage', e)
    }
  },
  /**
   * Remove a key from localStorage
   * @param {string} key - The key to remove
   */
  delete(key: string) {
    const val = this._generateKey(key)
    try {
      global.localStorage.removeItem(val)
    } catch (e) {
      captureExceptionWithMessage('Error deleting from local storage', e)
    }
  }
}

export default LocalPersistence
